import { useEffect, useRef, useState } from "react";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip/index.tsx";
import styled from "styled-components/macro";
import firebase from "../../Firebase.js";
import { useCommentStore } from "../../stores/commentStore";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { sendAppEvent } from "../../Utils.js";
import { commentsManager } from "../ShareLinkPage/CommentsManager";
import { EditableDisplayName } from "../ShareLinkPage/EditableDisplayName";
import { useToast } from "../ToastWrapper/index.js";
import ReplyBox from "./ReplyBox";
import { CommentInfo } from "./styles";

export default function NewComment({ newComment, setNewComment, documentId, isMobile = false, commentLocation, onCommentCreated }) {
  const newCommentBoxRef = useRef(null);
  const newCommentInputBoxRef = useRef(null);
  const newCommentBoxLocationY = useCommentStore((state) => state.newCommentBoxLocationY);
  const cancelComment = useCommentStore((state) => state.cancelComment);
  const submitComment = useCommentStore((state) => state.submitComment);
  const newAnnotationId = useCommentStore((state) => state.newAnnotationId);
  const revealAnnotation = useCommentStore((state) => state.revealAnnotation);
  const displayName = useCommentStore((state) => state.displayName);
  const [showTooltip, setShowTooltip] = useState(false);
  const commenter = useCommentStore((state) => state.commenter);
  const hoveredAnnotationIds = useCommentStore((state) => state.hoveredAnnotationIds);
  const hoverAnnotation = useCommentStore((state) => state.hoverAnnotation);
  const isSharedDocument = useCommentStore((state) => state.isSharedDocument);
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat ?? state.subscription?.hasAccessToAgenticChat ?? false);
  const placeholder = !isSharedDocument && hasAccessToAgenticChat ? "Comment or @ to ask Sudowrite" : "Comment";

  const { setToast } = useToast();

  const handleSendAppEvent = () => {
    let isAnonUserFirstComment = false;
    if (!firebase.auth().currentUser) {
      isAnonUserFirstComment = true;
    }

    sendAppEvent("CleanCopy - Submit Comment", {
      documentId,
      location: commentLocation,
      isReply: false,
      isAnonUserFirstComment,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (newCommentBoxRef.current && !newCommentBoxRef.current.contains(event.target)) {
        if (newComment.trim() === "") {
          cancelComment();
        }
      }
    };

    const cleanup = revealAnnotation(newAnnotationId);

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (typeof cleanup === "function") cleanup();
    };
  }, [newComment, cancelComment]);

  useEffect(() => {
    sendAppEvent("CleanCopy - NewComment Box Opened", {
      documentId,
      location: commentLocation,
    });
  }, []);

  // Register with CommentsManager (only needed on ShareLinkPage, not in the Editor sidebar)
  useEffect(() => {
    if (!isMobile && newCommentBoxRef.current && commentLocation !== "Editor") {
      commentsManager.registerNewCommentBox(newCommentBoxRef.current, newCommentBoxLocationY);
    }
    return () => {
      if (commentLocation !== "Editor") {
        commentsManager.unregisterNewCommentBox();
      }
    };
  }, [newCommentBoxLocationY, isMobile, commentLocation]);

  // apply hover styles to all related marks when the corresponding comment is active
  useEffect(() => {
    if (hoveredAnnotationIds?.includes(newAnnotationId)) {
      return hoverAnnotation(newAnnotationId);
    }
  }, [hoveredAnnotationIds, newAnnotationId, hoverAnnotation]);

  // Set the new comment box location and focus the input box
  useEffect(() => {
    if (!newCommentBoxRef.current) {
      return;
    }

    newCommentInputBoxRef.current.focus({ preventScroll: true });
  }, [newCommentBoxLocationY]);

  return (
    <NewCommentWrapper
      ref={newCommentBoxRef}
      className="new-comment-wrapper"
      isMobile={isMobile}
      commentLocation={commentLocation}
    >
      <SudoTooltip
        text={"Set a name so the author can see who you are."}
        animate
        placement="bottom"
        controlled
        open={showTooltip}
        onOpenChange={(open) => {
          setShowTooltip(open);
        }}
      >
        <CommentInfo>
          {commenter ? (
            <EditableDisplayName
              setShowTooltip={setShowTooltip}
              isMobile={isMobile}
              commentLocation={commentLocation}
            />
          ) : (
            <div className="commentAuthorContainer">
              <div className="commentAuthor">{displayName}</div>
            </div>
          )}
        </CommentInfo>
      </SudoTooltip>

      <ReplyBox
        submitComment={async (e) => {
          e.preventDefault();
          // save in case of error
          const comment = newComment;
          setNewComment("");
          handleSendAppEvent();

          const { success, message, keepThreadOpen, createdCommentId } = await submitComment(documentId, comment);

          if (!success) {
            setToast(message);
            setNewComment(comment);
            return;
          }

          if (keepThreadOpen && createdCommentId && typeof onCommentCreated === "function") {
            onCommentCreated(createdCommentId);
          }
        }}
        cancelComment={cancelComment}
        newComment={newComment}
        setNewComment={setNewComment}
        newCommentInputBoxRef={newCommentInputBoxRef}
        placeholder={placeholder}
        buttonText="Comment"
        alwaysShowActionButtons={isMobile}
        isMobile={isMobile}
      />
    </NewCommentWrapper>
  );
}

const NewCommentWrapper = styled.div`
  color: var(--grey-0);
  box-sizing: border-box;
  background-color: var(--menu-color-fullscreen);
  padding: 15px;
  box-shadow: var(--card-drop-shadow);
  border-radius: 15px;
  width: 270px;
  position: absolute;
  z-index: 101;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: ${(props) => (props.isMobile ? "16px" : "14px")};

  ${({ commentLocation }) =>
    commentLocation === "Editor" &&
    `
    position: relative;
    width: 100%;
  `}

  ${({ isMobile }) =>
    isMobile &&
    `
    position: fixed;
    top: 20px;
    width: 90%;
    left: 50%;
    transform: translateX(-50%);
  `}
`;
